/**
 * Feedback Dialog — submit feedback + view history with admin replies
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import {
  ThumbsUp, ThumbsDown, Minus, Bug, Lightbulb, MessageSquare,
  Send, Loader2, Clock, CheckCircle2, Archive, ChevronDown
} from 'lucide-react';
import FeedbackThread from './feedback/FeedbackThread';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { value: 'bug_report', label: 'Bug Report', icon: Bug, color: 'text-red-400 bg-red-500/10 border-red-500/30' },
  { value: 'feature_request', label: 'Feature Request', icon: Lightbulb, color: 'text-blue-400 bg-blue-500/10 border-blue-500/30' },
  { value: 'general', label: 'General', icon: MessageSquare, color: 'text-slate-300 bg-slate-500/10 border-slate-500/30' },
];

const SENTIMENTS = [
  { value: 'positive', icon: ThumbsUp, label: 'Positive', color: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30' },
  { value: 'neutral', icon: Minus, label: 'Neutral', color: 'text-slate-300 bg-slate-500/10 border-slate-500/30' },
  { value: 'negative', icon: ThumbsDown, label: 'Negative', color: 'text-red-400 bg-red-500/10 border-red-500/30' },
];

const STATUS_STYLES = {
  open: { label: 'Open', icon: Clock, cls: 'text-amber-400 bg-amber-500/10 border-amber-500/30' },
  reviewed: { label: 'Reviewed', icon: CheckCircle2, cls: 'text-blue-400 bg-blue-500/10 border-blue-500/30' },
  resolved: { label: 'Resolved', icon: CheckCircle2, cls: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30' },
  archived: { label: 'Archived', icon: Archive, cls: 'text-slate-500 bg-slate-500/10 border-slate-600/30' },
};

const FeedbackDialog = ({ open, onOpenChange, unreadReplies = 0, onRepliesRead }) => {
  // Default to history tab whenever there are unread admin replies to show
  const [tab, setTab] = useState(unreadReplies > 0 ? 'history' : 'new');
  const [category, setCategory] = useState('general');
  const [sentiment, setSentiment] = useState('neutral');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  // If the dialog opens with unread replies, force-switch to history view
  useEffect(() => {
    if (open && unreadReplies > 0) setTab('history');
  }, [open, unreadReplies]);

  const markRepliesRead = useCallback(async () => {
    if (unreadReplies === 0) return;
    try {
      await axios.put(`${API_URL}/api/feedback/mark-read`);
      if (onRepliesRead) onRepliesRead();
    } catch (err) {
      // silent — Layout's poll will catch up
    }
  }, [unreadReplies, onRepliesRead]);

  const fetchHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const res = await axios.get(`${API_URL}/api/feedback`);
      setHistory(res.data.feedback || []);
      // Auto-expand the most recent item with an unread reply (fallback: any item with a thread > 1)
      const list = res.data.feedback || [];
      const unread = list.find((fb) => fb.unread_for_me);
      const withThread = list.find((fb) => fb.thread && fb.thread.length > 1);
      const target = unread || withThread;
      if (target) setExpandedId(target.id);
    } catch (err) {
      console.error('Failed to load feedback history:', err);
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  useEffect(() => {
    if (open && tab === 'history') {
      fetchHistory();
      markRepliesRead();
    }
  }, [open, tab, fetchHistory, markRepliesRead]);

  const handleSubmit = async () => {
    if (!message.trim()) { toast.error('Please enter your feedback'); return; }
    setSubmitting(true);
    try {
      // Build a small triage block — invisible to the user, parsed by the
      // admin UI. Captures FE build, BE deploy time, browser, viewport, and
      // page so we can immediately tell if the user is on a stale bundle
      // when they report an issue.
      // eslint-disable-next-line no-undef
      const feBuild = typeof __BUILD_TIME__ !== 'undefined' ? __BUILD_TIME__ : 'dev';
      let beDeployed = '';
      try {
        const v = await axios.get(`${API_URL}/api/version`);
        beDeployed = v?.data?.backend_deployed_at || '';
      } catch { /* best-effort */ }
      const triage = {
        fe_build: feBuild,
        be_deployed: beDeployed,
        ua: navigator.userAgent.slice(0, 200),
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        page: window.location.pathname,
      };
      await axios.post(`${API_URL}/api/feedback`, {
        category, sentiment, message: message.trim(),
        page: window.location.pathname,
        client_meta: triage,
      });
      toast.success('Thank you for your feedback!');
      setMessage('');
      setCategory('general');
      setSentiment('neutral');
      setTab('history');
      fetchHistory();
    } catch (err) {
      toast.error('Failed to submit feedback');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-800 border-slate-700 max-w-md" data-testid="feedback-dialog">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-amber-400" />Feedback
          </DialogTitle>
        </DialogHeader>

        {/* Tabs */}
        <div className="flex gap-1 bg-slate-900/50 p-1 rounded-lg" data-testid="feedback-tabs">
          {[{ id: 'new', label: 'New' }, { id: 'history', label: 'My Submissions' }].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex-1 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                tab === t.id ? 'bg-amber-600 text-white' : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
              }`}
              data-testid={`feedback-tab-${t.id}`}>{t.label}</button>
          ))}
        </div>

        {tab === 'new' ? (
          <div className="space-y-4">
            {/* Category */}
            <div>
              <p className="text-xs text-slate-400 mb-2">Category</p>
              <div className="flex gap-2">
                {CATEGORIES.map(cat => (
                  <button key={cat.value} onClick={() => setCategory(cat.value)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium transition-all ${
                      category === cat.value ? cat.color : 'text-slate-500 bg-transparent border-slate-700 hover:border-slate-500'
                    }`} data-testid={`feedback-cat-${cat.value}`}>
                    <cat.icon className="h-3.5 w-3.5" />{cat.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Sentiment */}
            <div>
              <p className="text-xs text-slate-400 mb-2">How are you feeling about this?</p>
              <div className="flex gap-2">
                {SENTIMENTS.map(s => (
                  <button key={s.value} onClick={() => setSentiment(s.value)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium transition-all ${
                      sentiment === s.value ? s.color : 'text-slate-500 bg-transparent border-slate-700 hover:border-slate-500'
                    }`} data-testid={`feedback-sentiment-${s.value}`}>
                    <s.icon className="h-3.5 w-3.5" />{s.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Message */}
            <div>
              <p className="text-xs text-slate-400 mb-2">Your feedback</p>
              <Textarea value={message} onChange={(e) => setMessage(e.target.value)}
                placeholder="Tell us what's on your mind..."
                className="bg-slate-900/50 border-slate-600 text-white placeholder-slate-500 min-h-[100px] resize-none"
                data-testid="feedback-message" />
            </div>

            {/* Submit */}
            <Button onClick={handleSubmit} disabled={submitting || !message.trim()}
              className="w-full bg-amber-600 hover:bg-amber-700 h-10" data-testid="feedback-submit-btn">
              {submitting
                ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Sending...</>
                : <><Send className="h-4 w-4 mr-2" />Submit Feedback</>}
            </Button>
          </div>
        ) : (
          /* History Tab */
          <div className="space-y-2 max-h-80 overflow-y-auto" data-testid="feedback-history">
            {loadingHistory ? (
              <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-slate-500" /></div>
            ) : history.length === 0 ? (
              <p className="text-slate-500 text-sm text-center py-8">No feedback submitted yet</p>
            ) : (
              history.map(fb => {
                const st = STATUS_STYLES[fb.status] || STATUS_STYLES.open;
                const isExpanded = expandedId === fb.id;
                return (
                  <div key={fb.id}
                    className={`rounded-lg border transition-all ${
                      fb.status === 'resolved' ? 'border-emerald-500/20 bg-emerald-500/5' :
                      fb.unread_for_me ? 'border-amber-500/40 bg-amber-500/10' :
                      (fb.thread && fb.thread.length > 1) ? 'border-amber-500/20 bg-amber-500/5' :
                      'border-slate-700 bg-slate-900/40'
                    }`}
                    data-testid={`feedback-history-${fb.id}`}>
                    <button onClick={() => setExpandedId(isExpanded ? null : fb.id)}
                      className="w-full p-3 flex items-start justify-between gap-2 text-left">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <Badge variant="outline" className={`${st.cls} text-[10px]`}>
                            <st.icon className="h-2.5 w-2.5 mr-0.5" />{st.label}
                          </Badge>
                          {fb.unread_for_me && (
                            <Badge variant="outline" className="text-amber-400 bg-amber-500/10 border-amber-500/30 text-[10px] animate-pulse">
                              New reply
                            </Badge>
                          )}
                          {!fb.unread_for_me && fb.thread && fb.thread.length > 1 && (
                            <Badge variant="outline" className="text-slate-400 bg-slate-700/40 border-slate-600/30 text-[10px]">
                              {fb.thread.length} messages
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-300 truncate">{fb.message}</p>
                        <p className="text-[10px] text-slate-600 mt-0.5">
                          {new Date(fb.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </p>
                      </div>
                      <ChevronDown className={`h-4 w-4 text-slate-500 shrink-0 mt-1 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                    </button>

                    {isExpanded && (
                      <div className="px-3 pb-3 border-t border-slate-700/50 pt-2">
                        <FeedbackThread
                          feedbackId={fb.id}
                          thread={fb.thread || []}
                          viewerRole="user"
                          onMessageSent={fetchHistory}
                        />
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default FeedbackDialog;
